import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Divider,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useDashboard } from "../../Hooks/useDashboard";
import { decryptData, DESCRYPTION_TYPE } from "../../Utils/constant";
import io from "socket.io-client";
import { API,EVENT_API} from "../../Utils/API";

import {
  LayersControl,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  ScaleControl,
  TileLayer,
  ZoomControl,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import InsightsIcon from "@mui/icons-material/Insights";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import GroupIcon from "@mui/icons-material/Group";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import DataObjectIcon from "@mui/icons-material/DataObject";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MyLocationIcon from "@mui/icons-material/MyLocation";

// Fix marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

const socket = io(
  process.env.NODE_ENV === "development"
    ? window.location.origin
    : process.env.REACT_APP_SERVER_API,
  {
    transports: ["websocket", "polling"],
  }
);

// Auto center map on new live user
const MapController = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    if (position && !isNaN(position[0]) && !isNaN(position[1])) {
      map.flyTo(position, 18, {
        duration: 1.5,
      });
    }
  }, [position, map]);

  return null;
};

// Auto-fit map to live markers (when available).
const FitToLiveBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [bounds, map]);

  return null;
};

export const Dashboard = () => {
  const [counts, setCounts] = useState({});
  const [location, setLocation] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showLiveOnly, setShowLiveOnly] = useState(false);
  const [liveUsers, setLiveUsers] = useState({});
  const [suspiciousAlerts, setSuspiciousAlerts] = useState([]);
  const [, setTick] = useState(0);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { getCounts, searchUserByLocation } = useDashboard();

  // 🔐 SAFE DECRYPT
  function safeDecrypt(value) {
    try {
      if (value === null || value === undefined) return null;
      if (typeof value === "number") return value;
      const decrypted = decryptData(value, DESCRYPTION_TYPE.STRING);
      const num = Number(decrypted);
      return isNaN(num) ? decrypted : num;
    } catch {
      return null;
    }
  }
  // ⏱ TIMER LOGIC
  const getRemainingTime = (startTime) => {
    if (!startTime) return 0;
    return Math.max(
      0,
      // 180 - Math.floor((Date.now() - new Date(startTime)) / 1000)
      30 - Math.floor((Date.now() - new Date(startTime)) / 1000),
    );
  };
  //for 180 seconds, change to 30 seconds for testing
  // const getStatusColor = (status, remaining) => {
  //   if (status === "OFFLINE") return "#f44336";
  //   if (status === "OPEN") {
  //     if (remaining > 120) return "#4caf50";
  //     if (remaining > 60) return "#ff9800";
  //     return "#f44336";
  //   }
  //   return "gray";
  // };

  // 🔁 FORCE RERENDER EVERY SECOND
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handlePaymentAlert = (data, isSuspicious) => {
    const userId = safeDecrypt(data.userId);
    if (!userId) return;

    const reason = data.reason ? safeDecrypt(data.reason) : "";
    const distance = data.distance_meters;
    const deviceLoc = data.device_location;
    const txLoc = data.transaction_location;

    const alert = {
      id: `${userId}-${Date.now()}`,
      userId,
      suspicious: isSuspicious,
      reason,
      distance,
      deviceLoc,
      txLoc,
      at: new Date().toISOString(),
    };

    setSuspiciousAlerts((prev) => [alert, ...prev].slice(0, 50));

    if (isSuspicious && txLoc?.latitude != null && txLoc?.longitude != null) {
      setMapCenter([Number(txLoc.latitude), Number(txLoc.longitude)]);
    }
  };

  // 🔌 SOCKET
  useEffect(() => {
    socket.on("connect", () => {
      socket.emit("deviceId", "ADMIN_PANEL");
    });

    socket.on("event_status_update", (data) => {
      handleWalletEvent(data);
      if (data?.payment_auth?.suspicious) {
        handlePaymentAlert(
          {
            userId: data.userId,
            reason: "geo_mismatch",
            distance_meters: data.payment_auth.distance_meters,
            device_location: data.location
              ? { latitude: data.location.latitude, longitude: data.location.longitude }
              : null,
            transaction_location: data.payment_auth.transaction_location,
          },
          true,
        );
      }
      console.log("📍 SOCKET EVENT:", data);
    });

    socket.on("payment_suspicious", (data) => {
      handlePaymentAlert(data, true);
    });

    socket.on("payment_authorized", (data) => {
      handlePaymentAlert(data, false);
    });

    return () => {
      socket.off("event_status_update");
      socket.off("payment_suspicious");
      socket.off("payment_authorized");
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveUsers((prev) => {
        const updated = { ...prev };
        let removedIds = [];

        Object.keys(updated).forEach((userId) => {
          const user = updated[userId];

          if (!user?.startTime) return;

          const remaining = Math.max(
            0,
            30 - Math.floor((Date.now() - new Date(user.startTime)) / 1000),
          );

          if (remaining <= 0) {
            delete updated[userId];
            removedIds.push(Number(userId));
          }
        });

        // ✅ ALSO REMOVE FROM USERS LIST
        if (removedIds.length > 0) {
          setUsers((prevUsers) =>
            prevUsers.filter((u) => !removedIds.includes(u.id)),
          );
        }

        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleWalletEvent = (data) => {
    const userId = safeDecrypt(data.userId);
    const status = safeDecrypt(data.status);
    const lastActiveAt = data.lastActiveAt
      ? safeDecrypt(data.lastActiveAt)
      : null;

    if (!userId) return;

    const lat = Number(data?.location?.latitude);
    const lng = Number(data?.location?.longitude);

    setLiveUsers((prev) => {
      const updated = { ...prev };

      if (status === "live" && lastActiveAt) {
        updated[userId] = {
          status: "OPEN",
          startTime: lastActiveAt,
          location: {
            latitude: lat,
            longitude: lng,
            address: data?.location?.address,
            ip: data?.location?.ip,
            date: data?.location?.date,
          },
        };
      }

      return updated;
    });

    // ✅ AUTO MOVE MAP
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter([lat, lng]);
    }

    setUsers((prev) => {
      const exists = prev.find((u) => u.id === userId);
      if (!exists) fetchUserLocation(userId);
      return prev;
    });
  };

  // const fetchUserLocation = async (userId) => {
  //   try {
  //     const res = await API.get(`/api/v1/get-user-by-id/${userId}`);
  //     const user = res.data.data;

  //     if (!user) return;

  //     setUsers((prev) => {
  //       const exists = prev.find((u) => u.id === user.id);
  //       return exists
  //         ? prev.map((u) => (u.id === user.id ? user : u))
  //         : [...prev, user];
  //     });
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  //api calling from api file changes if below not working then use above code for fetchUserLocation function 
const fetchUserLocation = async (userId) => {
  try {
    const res = await API.get(
      `${EVENT_API.GET_USER_BY_ID}/${userId}`
    );

    const user = res.data.data;

    if (!user) return;

    setUsers((prev) => {
      const exists = prev.find((u) => u.id === user.id);

      return exists
        ? prev.map((u) =>
            u.id === user.id ? user : u
          )
        : [...prev, user];
    });
  } catch (err) {
    console.error(err);
  }
};

  // 📊 COUNTS
  useEffect(() => {
    (async () => {
      const res = await getCounts();
      setCounts(res.data.data);
    })();
  }, []);

  // 🔍 SEARCH
  const handleSearch = async () => {
    if (!location.trim()) return;

    setLoading(true);
    try {
      const res = await searchUserByLocation(location);
      setUsers(res.data.data || []);
      setMessage(res.data.message || "");
    } catch {
      setUsers([]);
      setMessage("Error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status, remaining) => {
    if (status === "OFFLINE") return "#f44336";
    if (status === "OPEN") {
      if (remaining > 10) return "#4caf50";
      if (remaining > 5) return "#ff9800";
      return "#f44336";
    }
    return "gray";
  };

  // 🔥 COMMON LIVE INFO
  const getLiveInfo = (user) => {
    const liveData = liveUsers[user.id];

    // ✅ parse properly
    let lat = liveData?.location?.latitude;
    let lng = liveData?.location?.longitude;

    if (!liveData) {
      return {
        status: "OFFLINE",
        remaining: 0,
        color: "#f44336",
      };
    }

    const remaining = getRemainingTime(liveData.startTime);
    const status = liveData.status;
    const color = getStatusColor(status, remaining);

    return { status, remaining, color };
  };

  // 🎯 UI COMPONENTS
  const StatusDot = ({ color }) => (
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
      }}
    />
  );

  const CircularTimer = ({ remaining }) => {
    const radius = 14;
    const stroke = 3;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    // Matches `getRemainingTime` (30s window).
    const progress = remaining / 30;
    const strokeDashoffset = circumference - progress * circumference;

    const color = remaining > 10 ? "#4caf50" : remaining > 5 ? "#ff9800" : "#f44336";

    return (
      <svg height={28} width={28}>
        <circle
          stroke="#eee"
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={stroke}
          strokeDasharray={`${circumference} ${circumference}`}
          style={{ strokeDashoffset }}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
    );
  };

  const filteredUsers = showLiveOnly
    ? users.filter((u) => liveUsers[u.id]?.status === "OPEN")
    : users;

  const memoUsers = useMemo(() => filteredUsers, [filteredUsers]);

  const liveCount = useMemo(() => Object.keys(liveUsers).length, [liveUsers]);

  const liveUsersList = useMemo(() => {
    return memoUsers
      .map((u) => {
        const liveData = liveUsers[u.id];
        const lat = Number(liveData?.location?.latitude);
        const lng = Number(liveData?.location?.longitude);
        const { status, remaining, color } = getLiveInfo(u);
        return {
          user: u,
          lat,
          lng,
          status,
          remaining,
          color,
          isLive: status === "OPEN" && !isNaN(lat) && !isNaN(lng),
        };
      })
      .filter((x) => x.isLive)
      .sort((a, b) => b.remaining - a.remaining);
  }, [memoUsers, liveUsers]);

  const liveBounds = useMemo(() => {
    const points = liveUsersList.map((x) => [x.lat, x.lng]);
    suspiciousAlerts.forEach((a) => {
      if (a.suspicious && a.txLoc?.latitude != null) {
        points.push([Number(a.txLoc.latitude), Number(a.txLoc.longitude)]);
      }
    });
    return points.length ? points : null;
  }, [liveUsersList, suspiciousAlerts]);

  const suspiciousMarkers = useMemo(() => {
    return suspiciousAlerts.filter(
      (a) =>
        a.suspicious &&
        a.txLoc?.latitude != null &&
        a.txLoc?.longitude != null,
    );
  }, [suspiciousAlerts]);

  const summaryCards = useMemo(
    () => [
      {
        key: "user_counts",
        label: "Total users",
        icon: <GroupIcon color="primary" />,
        value: counts?.user_counts ?? 0,
      },
      {
        key: "product_counts",
        label: "Total products",
        icon: <QrCode2Icon color="primary" />,
        value: counts?.product_counts ?? 0,
      },
      {
        key: "event_count",
        label: "Events",
        icon: <InsightsIcon color="primary" />,
        value: counts?.event_count ?? 0,
      },
      {
        key: "archived_users_counts",
        label: "Archived users",
        icon: <GroupIcon color="primary" />,
        value: counts?.archived_users_counts ?? 0,
      },
      {
        key: "live_users",
        label: "Live users (last 30s)",
        icon: <MyLocationIcon color="primary" />,
        value: liveCount,
      },
    ],
    [counts, liveCount],
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        {/* Modern header */}
        <Box
          mt={3}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            color: "primary.contrastText",
            background: "linear-gradient(135deg, #011638 0%, #0b3a7e 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Live activity, quick actions, and key system metrics.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Tooltip title="Products">
                <Chip
                  onClick={() => navigate("/products")}
                  icon={<QrCode2Icon />}
                  label="Products"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "white" }}
                />
              </Tooltip>
              <Tooltip title="Users">
                <Chip
                  onClick={() => navigate("/app-users")}
                  icon={<GroupIcon />}
                  label="Users"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "white" }}
                />
              </Tooltip>
              <Tooltip title="User issues">
                <Chip
                  onClick={() => navigate("/user-issues")}
                  icon={<SupportAgentIcon />}
                  label="Support"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "white" }}
                />
              </Tooltip>
              <Tooltip title="Metadata">
                <Chip
                  onClick={() => navigate("/metadata")}
                  icon={<DataObjectIcon />}
                  label="Metadata"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "white" }}
                />
              </Tooltip>
              <Tooltip title="Admin">
                <Chip
                  onClick={() => navigate("/admin-user")}
                  icon={<AdminPanelSettingsIcon />}
                  label="Admin"
                  sx={{ bgcolor: "rgba(255,255,255,0.12)", color: "white" }}
                />
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* KPI cards */}
        <Box mt={3}>
          <Grid container spacing={2}>
            {summaryCards.map((card) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={card.key === "live_users" ? 4 : 3}
                key={card.key}
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "secondary.main",
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {card.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {card.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Search + toggles */}
        <Box mt={3}>
          <Card
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Find users by location
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Search by city, region, or text included in the address.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ flex: 1.4 }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Mumbai, Pune, Delhi..."
                  />
                  <Button
                    variant="contained"
                    disabled={loading || !location.trim()}
                    onClick={handleSearch}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </Stack>

                <Divider
                  flexItem
                  orientation={isMobile ? "horizontal" : "vertical"}
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    clickable
                    color={showLiveOnly ? "primary" : "default"}
                    label={showLiveOnly ? "Live only" : "All users"}
                    onClick={() => setShowLiveOnly((v) => !v)}
                  />
                  <Chip variant="outlined" label={`Showing: ${filteredUsers.length}`} />
                  {message ? <Chip variant="outlined" color="warning" label={message} /> : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        {/* MAP */}
        <Box mt={4}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Live user map
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Markers appear when a user is active (last 30 seconds). Click a marker to view details.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Box sx={{ position: "relative" }}>
                <MapContainer
                  center={mapCenter}
                  zoom={5}
                  zoomControl={false}
                  style={{ height: 460, borderRadius: 12, overflow: "hidden" }}
                >
                  <ZoomControl position="bottomright" />
                  <ScaleControl position="bottomleft" />

                  <MapController position={mapCenter} />
                  <FitToLiveBounds bounds={liveBounds} />

                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Street (OSM)">
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Satellite (Esri)">
                      <TileLayer
                        attribution="Tiles &copy; Esri"
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  {liveUsersList.map(({ user, lat, lng, remaining, color }) => {
                    const icon = new L.DivIcon({
                      html: `<div class="secar-live-marker" style="--secar-live-color:${color}">
                        <div class="secar-live-marker__pulse"></div>
                        <div class="secar-live-marker__dot"></div>
                      </div>`,
                      className: "",
                      iconSize: [26, 26],
                      iconAnchor: [13, 13],
                    });

                    return (
                      <Marker key={user.id} position={[lat, lng]} icon={icon}>
                        <Popup>
                          {(() => {
                            const firstname = safeDecrypt(user.firstname);
                            const lastname = safeDecrypt(user.lastname);
                            const email = safeDecrypt(user.email);
                            const phone = safeDecrypt(user.phone);
                            const deviceName = safeDecrypt(user.deviceName);
                            const deviceType = safeDecrypt(user.deviceType);
                            const liveData = liveUsers[user.id];
                            const location = liveData?.location;
                            const { status, remaining, color } = getLiveInfo(user);

                            return (
                              <div style={{ minWidth: 220 }}>
                                <strong>
                                  {firstname} {lastname}
                                </strong>
                                <hr />
                                <div>
                                  <b>Email:</b> {email}
                                </div>
                                <div>
                                  <b>Phone:</b> {phone}
                                </div>
                                <div>
                                  <b>Device:</b> {deviceName}
                                </div>
                                <div>
                                  <b>Type:</b> {deviceType}
                                </div>

                                {location && (
                                  <>
                                    <div style={{ marginTop: 6 }}>
                                      <b>Address:</b> {location.address}
                                      <br />
                                      ({location.latitude}, {location.longitude})
                                    </div>
                                    <div>
                                      <b>IP:</b> {location.ip}
                                    </div>
                                  </>
                                )}

                                <div
                                  style={{
                                    marginTop: 10,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <StatusDot color={color} />
                                  <b style={{ color }}>
                                    {status === "OPEN" ? "LIVE" : "OFFLINE"}
                                  </b>
                                  {status === "OPEN" ? (
                                    <>
                                      <CircularTimer remaining={remaining} />
                                      <span>{remaining}s</span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })()}
                        </Popup>
                      </Marker>
                    );
                  })}

                  {suspiciousMarkers.map((alert) => {
                    const txLat = Number(alert.txLoc.latitude);
                    const txLng = Number(alert.txLoc.longitude);
                    const devLat = Number(alert.deviceLoc?.latitude);
                    const devLng = Number(alert.deviceLoc?.longitude);
                    const user = users.find((u) => u.id === alert.userId);

                    const icon = new L.DivIcon({
                      html: `<div class="secar-live-marker" style="--secar-live-color:#b71c1c">
                        <div class="secar-live-marker__pulse"></div>
                        <div class="secar-live-marker__dot"></div>
                      </div>`,
                      className: "",
                      iconSize: [30, 30],
                      iconAnchor: [15, 15],
                    });

                    return (
                      <React.Fragment key={alert.id}>
                        {!isNaN(devLat) && !isNaN(devLng) && (
                          <Polyline
                            positions={[
                              [devLat, devLng],
                              [txLat, txLng],
                            ]}
                            pathOptions={{ color: "#b71c1c", dashArray: "6 8", weight: 3 }}
                          />
                        )}
                        <Marker position={[txLat, txLng]} icon={icon}>
                          <Popup>
                            <div style={{ minWidth: 220 }}>
                              <strong style={{ color: "#b71c1c" }}>Suspicious payment</strong>
                              <hr />
                              {user ? (
                                <div>
                                  User #{alert.userId}: {safeDecrypt(user.firstname)}{" "}
                                  {safeDecrypt(user.lastname)}
                                </div>
                              ) : (
                                <div>User #{alert.userId}</div>
                              )}
                              <div>
                                <b>Reason:</b> {alert.reason || "geo_mismatch"}
                              </div>
                              {alert.distance != null && (
                                <div>
                                  <b>Distance:</b> {alert.distance}m (limit 10m)
                                </div>
                              )}
                              <div style={{ marginTop: 6 }}>
                                <b>Transaction:</b> ({txLat}, {txLng})
                              </div>
                              {alert.deviceLoc && (
                                <div>
                                  <b>Device:</b> ({devLat}, {devLng})
                                </div>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      </React.Fragment>
                    );
                  })}
                </MapContainer>

                {/* Legend overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: 12,
                    bgcolor: "rgba(255,255,255,0.92)",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>
                    Legend
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                    <StatusDot color="#4caf50" />
                    <Typography variant="caption">Live</Typography>
                    <StatusDot color="#ff9800" />
                    <Typography variant="caption">Expiring</Typography>
                    <StatusDot color="#f44336" />
                    <Typography variant="caption">Ending</Typography>
                    <StatusDot color="#b71c1c" />
                    <Typography variant="caption">Suspicious pay</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Live now: {liveUsersList.length}
                    {suspiciousMarkers.length > 0 ? ` · Alerts: ${suspiciousMarkers.length}` : ""}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Live users
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Click a user to focus the map.
                  </Typography>

                  <Box sx={{ display: "grid", gap: 1, maxHeight: 380, overflow: "auto" }}>
                    {liveUsersList.length === 0 ? (
                      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        No live users right now.
                      </Box>
                    ) : (
                      liveUsersList.map(({ user, lat, lng, remaining, color }) => (
                        <Box
                          key={user.id}
                          onClick={() => setMapCenter([lat, lng])}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            cursor: "pointer",
                            "&:hover": { bgcolor: "rgba(185,214,242,0.35)" },
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                                {safeDecrypt(user.firstname)} {safeDecrypt(user.lastname)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {safeDecrypt(user.email)}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <StatusDot color={color} />
                              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                {remaining}s
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      ))
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* TABLE */}
        <Box mt={4}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Users
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>phone</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredUsers.map((user) => {
                  const { status, remaining, color } = getLiveInfo(user);

                  // ✅ ADD THIS (missing in your code)
                  const liveData = liveUsers[user.id];
                  const location = liveData?.location;

                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        {safeDecrypt(user.firstname)}{" "}
                        {safeDecrypt(user.lastname)}
                      </TableCell>

                      <TableCell>{safeDecrypt(user.email)}</TableCell>
                      <TableCell>{safeDecrypt(user.phone)}</TableCell>

                      <TableCell>
                        {safeDecrypt(user.deviceName)}{" "}
                        {safeDecrypt(user.deviceType)}
                      </TableCell>

                      {/* ✅ LOCATION COLUMN */}
                      <TableCell>
                        {location?.address ?? "-"}
                        <br />({location?.latitude ?? "-"},
                        {location?.longitude ?? "-"})
                      </TableCell>
                      <TableCell>{location?.ip ?? "-"}</TableCell>

                      {/* ✅ STATUS */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <StatusDot color={color} />

                          <Typography sx={{ color }}>
                            {status === "OPEN" ? "LIVE" : "OFFLINE"}
                          </Typography>

                          {status === "OPEN" && (
                            <>
                              <CircularTimer remaining={remaining} />
                              <Typography variant="caption">
                                {remaining}s
                              </Typography>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Container>
  );
};
